import { action, makeObservable, observable, runInAction } from 'mobx'
import RootStore from '@App/stores/root-store'
import { LegacyPublisherSearch, PublisherSearch } from '@App/stores/search/models/publisher-search'
import { isEmptyObject } from '@App/helpers/check-is-empty-object'
import { PaginationBody } from '@Entities/pagination'
import { PublisherSearchResultModel } from '@Entities/work/publisher-search-result-model'
import { searchPublishers } from '@App/api/routes/search'
import { InterestedPartyWithWorkCount } from '@Entities/interested-party-with-work-count'
import { PublisherSearchStoreLike } from '@App/stores/search/publisher-search-store-like'

export default class UnrestrictedPublisherSearchStore implements PublisherSearchStoreLike {
    private rootStore: RootStore

    constructor(rootStore: RootStore) {
        makeObservable<UnrestrictedPublisherSearchStore, '_publisherSearch' | '_isSearching' | '_publisherSearchModel'>(
            this,
            {
                _publisherSearch: observable,
                _isSearching: observable,
                _publisherSearchModel: observable,
                setPublisherSearch: action,
                searchPublishers: action,
                clearStore: action,
            }
        )

        this.rootStore = rootStore
    }

    private _publisherSearch: LegacyPublisherSearch | undefined = {
        publisherName: '',
        publisherIpi: '',
    }

    private _isSearching = false

    private _publisherSearchModel: PublisherSearchResultModel = {
        count: 0,
        results: [],
    }

    private _publisherSearchPagination: PaginationBody = {
        page: 1,
        limit: 10,
    }

    get publisherSearch(): LegacyPublisherSearch | undefined {
        return this._publisherSearch
    }

    get isSearching(): boolean {
        return this._isSearching
    }

    get publisherSearchModel(): PublisherSearchResultModel {
        return this._publisherSearchModel
    }

    public setPublisherSearch(publisherSearch: LegacyPublisherSearch) {
        this._publisherSearch = publisherSearch
    }

    public async searchPublishers() {
        this._isSearching = true
        await this.routePublisherSearch()
    }

    public clearStore = () => {
        this._publisherSearchModel = {
            count: 0,
            results: [],
        }
        this._publisherSearch = {
            publisherIpi: '',
            publisherName: '',
        }
        this._publisherSearchPagination = {
            page: 1,
            limit: 5,
        }
        this._isSearching = false
    }

    private getSearchCriteria(): LegacyPublisherSearch | undefined {
        if (!this._publisherSearch || isEmptyObject(this._publisherSearch)) {
            return undefined
        }

        return this._publisherSearch
    }

    private async routePublisherSearch() {
        const searchCriteria = this.getSearchCriteria()
        if (!searchCriteria) {
            this.setEmptySearchResults()
            return
        }

        try {
            const results = await searchPublishers(
                this._publisherSearchPagination,
                this.mapLegacySearch(searchCriteria)
            )

            this.setSearchResults(results.data.content, results.data.totalElements)
        } catch (e) {
            this.finishSearch()
            this.rootStore.popupStore.setAlertFromError(e)
        }
    }

    private setEmptySearchResults() {
        runInAction(() => {
            this._publisherSearchModel = {
                count: 0,
                results: [],
            }
            this._isSearching = false
        })
    }

    private setSearchResults(publishers: InterestedPartyWithWorkCount[], count: number) {
        runInAction(() => {
            this._publisherSearchModel = {
                count,
                results: publishers.map((publisher) => ({
                    entity: this.mapInterestedPartyToPublisher(publisher),
                    highlights: {
                        fieldName: 'publisherName',
                        fieldValue: publisher.fullName,
                    },
                })),
            }
            this._isSearching = false
        })
    }

    private finishSearch() {
        runInAction(() => {
            this._isSearching = false
        })
    }

    private mapLegacySearch(search: LegacyPublisherSearch): PublisherSearch {
        return {
            fullName: search.publisherName || undefined,
            ipiNameNumber: search.publisherIpi || undefined,
            hfaNumber: search.publisherNumber || undefined,
        }
    }

    private mapInterestedPartyToPublisher(publisher: InterestedPartyWithWorkCount) {
        return {
            publisherName: publisher.fullName,
            ipId: String(publisher.ipId),
            ipiNumber: publisher.ipiNumber,
            publisherShare: null,
            lastUpdated: '',
            hfaPublisherNumber: publisher.hfaNumber,
            administratorPublishers: [],
            writers: [],
            worksCount: publisher.worksCount,
            totalWorks: publisher.worksCount,
        }
    }
}
